import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const loanApi = createApi({
  reducerPath: 'loanApi',
  baseQuery,
  tagTypes: ['Loan', 'LoanRepayment'],
  endpoints: (builder) => ({
    // Loan requests
    getLoanRequests: builder.query({
      query: (params) => ({
        url: '/loan/requests',
        params,
      }),
      providesTags: ['Loan'],
    }),
    getLoanRequest: builder.query({
      query: (id) => `/loan/requests/${id}`,
      providesTags: (result, error, id) => [{ type: 'Loan', id }],
    }),
    createLoanRequest: builder.mutation({
      query: (loanData) => ({
        url: '/loan/requests',
        method: 'POST',
        body: loanData,
      }),
      invalidatesTags: ['Loan'],
    }),
    updateLoanRequest: builder.mutation({
      query: ({ id, ...loanData }) => ({
        url: `/loan/requests/${id}`,
        method: 'PUT',
        body: loanData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Loan', id }],
    }),
    cancelLoanRequest: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/loan/requests/${id}/cancel`,
        method: 'PATCH',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Loan', id }],
    }),
    approveLoanRequest: builder.mutation({
      query: ({ id, remarks }) => ({
        url: `/loan/requests/${id}/approve`,
        method: 'PATCH',
        body: { remarks },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Loan', id }],
    }),
    rejectLoanRequest: builder.mutation({
      query: ({ id, remarks }) => ({
        url: `/loan/requests/${id}/reject`,
        method: 'PATCH',
        body: { remarks },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Loan', id }],
    }),

    // Repayment schedule
    getLoanRepaymentSchedule: builder.query({
      query: (id) => `/loan/requests/${id}/repayment`,
      providesTags: (result, error, id) => [{ type: 'LoanRepayment', id }],
    }),

    // Admin functions
    processLoanDeductions: builder.mutation({
      query: (data) => ({
        url: '/loan/process-deductions',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Loan', 'LoanRepayment'],
    }),
    processPayrollLoanDeductions: builder.mutation({
      query: (data) => ({
        url: '/loan/process-payroll-deductions',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Loan', 'LoanRepayment'],
    }),
  }),
});

export const {
  useGetLoanRequestsQuery,
  useGetLoanRequestQuery,
  useCreateLoanRequestMutation,
  useUpdateLoanRequestMutation,
  useCancelLoanRequestMutation,
  useApproveLoanRequestMutation,
  useRejectLoanRequestMutation,
  useGetLoanRepaymentScheduleQuery,
  useProcessLoanDeductionsMutation,
  useProcessPayrollLoanDeductionsMutation,
} = loanApi;